/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-copilot.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Which of the five outcomes happened — read this before rendering `text`. Model-authored text
 * exists only under `answered`. `refused` means the model produced output and the validator
 * rejected it (`rejection_reason` says why); `unavailable` means no validated output ever existed
 * — an outage or a capability-gate refusal, never a statement about model output; `platform_error`
 * is this platform's own defect, logged with its traceback.
 */
export type AnswerStatus = 'answered' | 'no_context' | 'refused' | 'unavailable' | 'platform_error';

export const ANSWER_STATUSES: readonly AnswerStatus[] = ['answered', 'no_context', 'refused', 'unavailable', 'platform_error'];

export function isAnswerStatus(value: unknown): value is AnswerStatus {
  return (['answered', 'no_context', 'refused', 'unavailable', 'platform_error'] as readonly unknown[]).includes(value);
}

export type RetrievalKind = 'dataset' | 'rule' | 'score' | 'incident' | 'lineage';

export const RETRIEVAL_KINDS: readonly RetrievalKind[] = ['dataset', 'rule', 'score', 'incident', 'lineage'];

export function isRetrievalKind(value: unknown): value is RetrievalKind {
  return (['dataset', 'rule', 'score', 'incident', 'lineage'] as readonly unknown[]).includes(value);
}

export interface Retrievable {
  readonly kind: RetrievalKind;
  /**
   * The EXISTING permission that governs this kind through its own module's API — there is no
   * copilot-specific read permission, deliberately.
   */
  readonly governed_by: string;
}

export function isRetrievable(value: unknown): value is Retrievable {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['dataset', 'rule', 'score', 'incident', 'lineage'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['governed_by'] === 'string'
  );
}

export interface AnswerFields {
  readonly status: AnswerStatus;
  /**
   * Model-authored ONLY when status is `answered` (and then `validator` names what accepted it).
   * Host-written fallback prose for every other status.
   */
  readonly text: string;
  /**
   * The [kind:id] references the model used. Grounded — a citation to a record the model was never
   * given is refused as ungrounded before it reaches here.
   */
  readonly citations: readonly string[];
  /**
   * The metadata kinds this caller could not read — a deliberate permissions disclosure; see the
   * contract description.
   */
  readonly refused_kinds: readonly string[];
  /** Required by CHECK for an `answered` row; null otherwise. */
  readonly validator?: string | null;
  /** Why the validator refused, when status is `refused`. */
  readonly rejection_reason?: string | null;
  readonly truncated: boolean;
}

export function isAnswerFields(value: unknown): value is AnswerFields {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['answered', 'no_context', 'refused', 'unavailable', 'platform_error'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['text'] === 'string' &&
    Array.isArray(candidate['citations']) &&
    (candidate['citations'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['refused_kinds']) &&
    (candidate['refused_kinds'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['truncated'] === 'boolean'
  );
}

export interface CopilotCapabilities {
  /** Always 1. Metadata only, never data values. */
  readonly rung: number;
  readonly retrievable: readonly Retrievable[];
  /** States the kind-at-tenant-granularity bound in the deployment's own words. */
  readonly authorization_policy: string;
  readonly never_retrieved: readonly string[];
  /**
   * "none" today — read-only module, no tool registry. The first tool added must return a proposal
   * requiring explicit confirmation.
   */
  readonly mutating_tools: string;
  readonly max_question_chars: number;
  readonly max_snippets: number;
  readonly kinds: readonly RetrievalKind[];
  readonly statuses: readonly AnswerStatus[];
}

export function isCopilotCapabilities(value: unknown): value is CopilotCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    Array.isArray(candidate['retrievable']) &&
    (candidate['retrievable'] as readonly unknown[]).every((item) => isRetrievable(item)) &&
    typeof candidate['authorization_policy'] === 'string' &&
    Array.isArray(candidate['never_retrieved']) &&
    (candidate['never_retrieved'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['mutating_tools'] === 'string' &&
    typeof candidate['max_question_chars'] === 'number' &&
    typeof candidate['max_snippets'] === 'number' &&
    Array.isArray(candidate['kinds']) &&
    (candidate['kinds'] as readonly unknown[]).every((item) => isRetrievalKind(item)) &&
    Array.isArray(candidate['statuses']) &&
    (candidate['statuses'] as readonly unknown[]).every((item) => isAnswerStatus(item))
  );
}
